(() => {
  if (window.SVExperience) return;

  const screen = document.getElementById('sv-experience-screen');
  const configNode = document.getElementById('sv-experience-config');
  if (!screen || !configNode) return;

  let config = {};
  try { config = JSON.parse(configNode.textContent); } catch (error) { console.error(error); return; }

  const message = screen.querySelector('[data-sv-experience-message]');
  const logo = screen.querySelector('.sv-experience-screen__logo');
  const durationMap = { fast: 280, balanced: 420, slow: 580 };
  const duration = durationMap[config.duration] || 420;
  let hideTimer = null;

  document.documentElement.style.setProperty('--sv-exp-duration', `${duration}ms`);
  const isDark = (() => {
    const hex = String(config.background || '#ffffff').replace('#', '');
    const normalized = hex.length === 3 ? hex.split('').map(x => x + x).join('') : hex;
    if (!/^[0-9a-f]{6}$/i.test(normalized)) return false;
    const [r,g,b] = [0,2,4].map(i => parseInt(normalized.slice(i,i+2),16));
    return ((r * 299 + g * 587 + b * 114) / 1000) < 128;
  })();
  if (logo) logo.src = isDark ? logo.dataset.svDarkLogo : logo.dataset.svLightLogo;

  const lock = value => {
    document.documentElement.classList.toggle('sv-experience-lock', value);
    document.body?.classList.toggle('sv-experience-lock', value);
  };

  const show = (text = config.loadingText, options = {}) => {
    clearTimeout(hideTimer);
    if (message) message.textContent = text || '';
    screen.setAttribute('aria-hidden', 'false');
    screen.classList.add('sv-is-visible');
    if (options.lock !== false) lock(true);
  };

  const hide = (immediate = false) => {
    clearTimeout(hideTimer);
    const finish = () => {
      screen.classList.remove('sv-is-visible');
      screen.setAttribute('aria-hidden', 'true');
      lock(false);
    };
    if (immediate) finish(); else hideTimer = setTimeout(finish, 80);
  };

  const showCheckout = () => {
    if (!config.checkoutTransition) return false;
    show(config.checkoutText || 'Preparing secure checkout...');
    return true;
  };
  const navigate = (url, text) => {
    show(text || config.loadingText);
    setTimeout(() => { window.location.href = url; }, Math.min(duration, 480));
  };

  window.SVExperience = { show, hide, showCheckout, navigate, duration };

  const shouldTransitionLink = (anchor, event) => {
    if (!config.pageTransitions || !anchor?.href) return false;
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return false;
    if (anchor.target && anchor.target !== '_self') return false;
    if (anchor.hasAttribute('download') || anchor.dataset.noTransition !== undefined) return false;
    if (anchor.closest('#sv-preorder-modal, cart-drawer, cart-notification, details-modal')) return false;
    const url = new URL(anchor.href, location.href);
    if (url.origin !== location.origin || ['mailto:', 'tel:', 'javascript:'].includes(url.protocol)) return false;
    if (url.pathname === location.pathname && url.search === location.search && url.hash) return false;
    return true;
  };

  document.addEventListener('click', event => {
    const anchor = event.target.closest('a[href]');
    if (!shouldTransitionLink(anchor, event)) return;
    event.preventDefault();
    navigate(anchor.href);
  }, false);

  const setupReveals = () => {
    if (!config.scrollAnimations || matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    document.documentElement.classList.add('sv-motion-ready');

    const selectors = [
      '#MainContent > .shopify-section',
      '.product__info-container > *',
      '.collection-list .card-wrapper',
      '.product-grid .grid__item',
      '.multicolumn-list__item',
      '.blog-articles__article',
      '.article-template > *'
    ];
    const nodes = [...new Set(selectors.flatMap(selector => [...document.querySelectorAll(selector)]))];
    nodes.forEach((node, index) => {
      if (node.closest('#sv-experience-screen, #sv-preorder-modal')) return;
      node.setAttribute('data-sv-reveal', '');
      const delayUnit = config.intensity === 'subtle' ? 25 : config.intensity === 'expressive' ? 65 : 42;
      node.style.setProperty('--sv-reveal-delay', `${Math.min(index % 6, 5) * delayUnit}ms`);
      const media = node.matches('.card-wrapper, .grid__item') ? node.querySelector('.media') : node.querySelector(':scope > .media');
      if (media) media.setAttribute('data-sv-image-reveal', '');
    });

    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('sv-is-revealed');
        entry.target.querySelectorAll('[data-sv-image-reveal]').forEach(el => el.classList.add('sv-is-revealed'));
        observer.unobserve(entry.target);
      });
    }, { threshold: .08, rootMargin: '0px 0px -6% 0px' });
    nodes.forEach(node => observer.observe(node));
  };

  const beginInitialExperience = () => {
    setupReveals();
    if (!config.initialLoader || Shopify.designMode) { hide(true); return; }
    const key = 'svExperienceSeen';
    let alreadySeen = false;
    try { alreadySeen = sessionStorage.getItem(key) === '1'; } catch (_) {}
    if (alreadySeen) { hide(true); return; }
    show(config.loadingText);
    try { sessionStorage.setItem(key, '1'); } catch (_) {}
    const ready = () => setTimeout(() => hide(), 450);
    if (document.readyState === 'complete') ready(); else window.addEventListener('load', ready, { once: true });
    setTimeout(() => hide(), 2600);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', beginInitialExperience, { once: true });
  else beginInitialExperience();

  window.addEventListener('pageshow', event => {
    if (event.persisted) hide(true);
  });
  window.addEventListener('pagehide', () => clearTimeout(hideTimer));
})();
